import { Modal, BlockStack, Text, List, Divider } from "@shopify/polaris";
import { useTranslation } from "../../hooks/useTranslation";

interface PrivacyPolicyModalProps {
  open: boolean;
  onClose: () => void;
}

export function PrivacyPolicyModal({ open, onClose }: PrivacyPolicyModalProps) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("footer.privacy")}
      primaryAction={{
        content: t("common.close") || "Close",
        onAction: onClose,
      }}
      size="large"
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            Data We Collect and Process
          </Text>

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              1. Merchant Business Information
            </Text>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              What we collect:
            </Text>
            <List type="bullet">
              <List.Item>Your Shopify store domain and access credentials</List.Item>
              <List.Item>Greek tax credentials (myDATA user ID and API subscription key)</List.Item>
              <List.Item>
                Business tax information: VAT number (ΑΦΜ), registered company name, business address, city, and postal code
              </List.Item>
              <List.Item>Optional company logo for branded delivery note PDFs</List.Item>
              <List.Item>App settings and preferences</List.Item>
            </List>

            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Why we collect it:
            </Text>
            <List type="bullet">
              <List.Item>To authenticate your Shopify store and access order data</List.Item>
              <List.Item>To submit delivery notes to the Greek tax authority (AADE) on your behalf</List.Item>
              <List.Item>To generate legally compliant delivery notes with your business information as the issuer</List.Item>
              <List.Item>To manage your subscription and billing</List.Item>
            </List>

            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Storage location:</Text> Supabase database (secure cloud storage with encryption)
            </Text>
          </BlockStack>

          <Divider />

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              2. Customer Personal Data
            </Text>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              What we collect from your Shopify orders:
            </Text>
            <List type="bullet">
              <List.Item>Customer first and last name</List.Item>
              <List.Item>Shipping address (street address, city, postal code, country)</List.Item>
              <List.Item>Order details (product names, quantities, SKUs, prices, order numbers)</List.Item>
            </List>

            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Why we collect it:
            </Text>
            <List type="bullet">
              <List.Item>
                <Text as="span" fontWeight="bold">Legal requirement:</Text> Greek tax law requires delivery notes to include recipient (customer) name and delivery address
              </List.Item>
              <List.Item>To submit legally compliant delivery notes to the Greek myDATA system</List.Item>
              <List.Item>To generate PDF copies of delivery notes for your records</List.Item>
              <List.Item>To maintain an audit trail of all tax submissions</List.Item>
            </List>

            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Important notes:
            </Text>
            <List type="bullet">
              <List.Item>We do NOT collect customer email addresses or phone numbers</List.Item>
              <List.Item>We do NOT collect payment information or credit card details</List.Item>
              <List.Item>Customer data is only accessed when you explicitly submit a delivery note for an order</List.Item>
            </List>
          </BlockStack>

          <Divider />

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              3. Subscription and Billing Data
            </Text>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              What we collect:
            </Text>
            <List type="bullet">
              <List.Item>Subscription status (trial, active, cancelled, expired)</List.Item>
              <List.Item>Billing cycle dates (start and end of each 30-day period)</List.Item>
              <List.Item>Shopify recurring charge ID</List.Item>
              <List.Item>Submission usage counts (number of delivery notes submitted)</List.Item>
            </List>

            <Text as="p" variant="bodyMd" fontWeight="semibold">
              Why we collect it:
            </Text>
            <List type="bullet">
              <List.Item>To manage your free trial (7 submissions) and paid subscription</List.Item>
              <List.Item>To track your plan's submission quota (30, 60 or 300 submissions per billing cycle)</List.Item>
              <List.Item>To enforce usage limits and billing</List.Item>
            </List>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Data Sharing and Third Parties
          </Text>

          <BlockStack gap="300">
            <Text as="h3" variant="headingSm" fontWeight="semibold">
              1. Greek Tax Authority (AADE - myDATA API)
            </Text>
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="bold">Legal requirement:</Text> Greek tax law mandates electronic submission of delivery notes to myDATA. This is the core purpose of the app - automating your legal tax compliance obligations.
            </Text>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              What we share:
            </Text>
            <List type="bullet">
              <List.Item>Your business information (VAT number, company name, address)</List.Item>
              <List.Item>Customer names and shipping addresses</List.Item>
              <List.Item>Order line item details (product names, quantities, SKUs)</List.Item>
            </List>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Data Retention
          </Text>

          <BlockStack gap="300">
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Active merchants:</Text> All data is retained indefinitely while your app is installed. Submission history is maintained as a permanent tax audit trail.
            </Text>
            <Text as="p" variant="bodyMd">
              <Text as="span" fontWeight="semibold">Uninstalled app:</Text> When you uninstall the app, your account is marked as "uninstalled" (soft delete). Your data is NOT automatically deleted - it is retained for legal and audit purposes.
            </Text>
          </BlockStack>

          <Divider />

          <Text as="h2" variant="headingMd">
            Data Security
          </Text>

          <List type="bullet">
            <List.Item>All data stored in Supabase with Row Level Security (RLS) enabled</List.Item>
            <List.Item>Access restricted to service role only (no public access)</List.Item>
            <List.Item>HTTPS/TLS encryption for all data in transit</List.Item>
            <List.Item>Shopify access tokens stored securely, never exposed in logs</List.Item>
            <List.Item>myDATA API credentials encrypted at rest</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            Your Rights
          </Text>

          <Text as="p" variant="bodyMd">
            Under GDPR and Greek data protection law, you have the right to:
          </Text>
          <List type="bullet">
            <List.Item><Text as="span" fontWeight="semibold">Access</Text> your data - view all data we store about you</List.Item>
            <List.Item><Text as="span" fontWeight="semibold">Rectification</Text> - correct inaccurate data</List.Item>
            <List.Item><Text as="span" fontWeight="semibold">Erasure</Text> - request deletion of your data (subject to legal retention requirements)</List.Item>
            <List.Item><Text as="span" fontWeight="semibold">Data portability</Text> - receive your data in a portable format</List.Item>
            <List.Item><Text as="span" fontWeight="semibold">Object</Text> to processing - in certain circumstances</List.Item>
          </List>

          <Divider />

          <Text as="h2" variant="headingMd">
            Legal Basis for Processing
          </Text>

          <Text as="p" variant="bodyMd">
            We process personal data under the following legal bases:
          </Text>
          <List type="number">
            <List.Item><Text as="span" fontWeight="semibold">Contract performance</Text> - To provide the myDATA submission service you subscribed to</List.Item>
            <List.Item><Text as="span" fontWeight="semibold">Legal obligation</Text> - To comply with Greek tax law requiring delivery note submissions</List.Item>
            <List.Item><Text as="span" fontWeight="semibold">Legitimate interests</Text> - To maintain audit trails, prevent fraud, and provide customer support</List.Item>
          </List>

          <Divider />

          <Text as="p" variant="bodySm" tone="subdued">
            Last updated: October 2026
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
